import _ from "lodash";
import type { TemperatureUnit, LengthUnit, Segment } from "../types";

export const convertTemperature = (
  sourceDegrees: TemperatureUnit,
  destDegrees: TemperatureUnit,
  temperature: number
): number => {
  if (sourceDegrees === destDegrees) {
    return temperature;
  }
  if (sourceDegrees === "celsius" && destDegrees === "fahrenheit") {
    return Math.round(temperature * 1.8 + 32);
  }
  if (sourceDegrees === "fahrenheit" && destDegrees === "celsius") {
    return Math.round((temperature - 32) / 1.8);
  }
  return temperature;
};
export const convertTemperatures = (
  sourceDegrees: TemperatureUnit,
  destDegrees: TemperatureUnit,
  temperatures: number[]
): number[] => {
  return temperatures.map((temperature) =>
    convertTemperature(sourceDegrees, destDegrees, temperature)
  );
};
export const convertRate = (
  sourceDegrees: TemperatureUnit,
  destDegrees: TemperatureUnit,
  rate: number
): number => {
  if (sourceDegrees === destDegrees) {
    return rate;
  }
  if (sourceDegrees === "celsius" && destDegrees === "fahrenheit") {
    return Math.round(rate * 1.8);
  }
  if (sourceDegrees === "fahrenheit" && destDegrees === "celsius") {
    return Math.round(rate / 1.8);
  }
  return rate;
};

// "AFAP" ("As Fast As Possible") is glass-artist shorthand some users write into
// the rate field. Treat it as 9999 for any numeric work — high enough that no
// real kiln will be the limiting factor.
export const parseRateValue = (rate: unknown): number => {
  if (typeof rate === "string" && rate.trim().toUpperCase() === "AFAP") {
    return 9999;
  }
  const n = typeof rate === "number" ? rate : parseFloat(String(rate));
  return Number.isFinite(n) ? n : 0;
};

export const parseSegmentNumber = (value: unknown): number => {
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
};

export const convertDegreesInSegment = (
  segment: Pick<Segment, "rate" | "temperature" | "degrees"> & Record<string, any>,
  degrees: TemperatureUnit
) => {
  const segmentDegrees: TemperatureUnit = segment.degrees ? segment.degrees : "celsius";
  const newSegment = { ...segment };
  newSegment.rate = parseRateValue(segment.rate);
  newSegment.temperature = parseSegmentNumber(segment.temperature);
  if (segmentDegrees !== degrees) {
    newSegment.rate = convertRate(segmentDegrees, degrees, newSegment.rate);
    newSegment.temperature = convertTemperature(segmentDegrees, degrees, newSegment.temperature);
  }
  return newSegment;
};

export const degreeText = (degrees?: string): string => {
  switch (degrees) {
    case "celsius":
      return "°C";
    case "fahrenheit":
      return "°F";
    default:
      return "°";
  }
};

export const degreeName = (degrees?: string): string => {
  return degrees === "fahrenheit" ? "Fahrenheit" : "Celsius";
};
// Array values are: [mintemp, maxtemp, priority]
const temperatureRanges: Record<string, Record<string, [number, number, number]>> = {
  Bullseye: {
    combing: [871, 927, 5],
    fullFuse: [804, 843, 1],
    kilnCasting: [816, 871, 5],
    kilnCarving: [816, 843, 5],
    strip: [799, 843, 2],
    tackFuse: [699, 779, 1],
    sagging: [679, 732, 1],
    fuseToStick: [679, 721, 5],
    slumping: [593, 704, 2],
    painting: [538, 677, 5],
    devit: [677, 927, 9],
  },
  Spectrum: {
    combing: [904, 927, 5],
    fullFuse: [793, 802, 1],
    tackFuse: [732, 743, 1],
    slumping: [660, 675, 1],
    anneal: [513 - 6, 513 + 6, 1],
    strain: [476 - 6, 476 + 6, 1],
  },
  "Baoli COE 85": {
    fullFuse: [830, 850, 1],
    tackFuse: [700, 760, 1],
    anneal: [528, 532, 1],
    strain: [455, 465, 1],
  },
  "Baoli COE 90": {
    fullFuse: [800, 840, 1],
    tackFuse: [688, 692, 1],
    anneal: [515, 525, 1],
    strain: [455, 455, 1],
  },
  Wissmach: {
    fullFuse: [760, 770, 1],
    tackFuse: [699, 709, 1],
    draping: [632, 642, 1],
    slumping: [688, 698, 1],
    kilnCasting: [782 - 5, 782 + 5, 5],
    combing: [871 - 5, 871 + 5, 5],
    anneal: [482 - 5, 482 + 5, 1],
    strain: [371 - 5, 371 + 5, 1],
  },
};

export const temperatureRangeNames: Record<string, string> = {
  combing: "Combing",
  fullFuse: "Full Fuse",
  kilnCasting: "Kiln Casting",
  kilnCarving: "Kiln Carving",
  strip: "Strip",
  tackFuse: "Tack Fuse",
  sagging: "Sagging",
  fuseToStick: "Fuse To Stick",
  slumping: "Slump",
  painting: "Painting",
  devit: "Devit",
  anneal: "Anneal",
  strain: "Strain",
  draping: "Drape",
};

export const getPriorityEnabledAnnotationsFromSegments = (
  sorted_segments: Pick<Segment, "temperature">[],
  glass: string,
  degrees: TemperatureUnit
): Record<string, boolean> => {
  const relevant_temperature_ranges = getEnabledAnnotationsFromSegments(
    sorted_segments,
    glass,
    degrees
  );
  // Find the lowest numbered priority. Enable all with the same priority.
  const glassRanges = getTemperatureRanges(glass, degrees);
  const min_priority = Object.keys(relevant_temperature_ranges).reduce(
    (min_priority, range_name) =>
      glassRanges[range_name][2] < min_priority ? glassRanges[range_name][2] : min_priority,
    99
  );
  const enabled_range_names = Object.keys(relevant_temperature_ranges).filter(
    (range_name) => glassRanges[range_name][2] === min_priority
  );

  return enabled_range_names.reduce(
    (enabled, name) => ({ ...enabled, [name]: true }),
    {} as Record<string, boolean>
  );
};

export const getPrincipalEnabledAnnotationFromSegments = (
  sorted_segments: Pick<Segment, "temperature">[],
  glass: string,
  degrees: TemperatureUnit
): Record<string, boolean> => {
  const max_segment = sorted_segments.reduce(
    (max, segment) => (segment.temperature > max.temperature ? segment : max),
    { temperature: 0 }
  );
  return getEnabledAnnotationsFromSegments([max_segment], glass, degrees);
};

export const getEnabledAnnotationsFromSegments = (
  sorted_segments: Pick<Segment, "temperature">[],
  glass: string,
  degrees: TemperatureUnit
): Record<string, boolean> => {
  const segmentsAreWithin = sorted_segments
    .map((segment) =>
      _.map(getTemperatureRanges(glass, degrees), ([a, b], key) =>
        a <= segment.temperature && b >= segment.temperature ? key : undefined
      )
    )
    .flat()
    .filter((a): a is string => a !== undefined)
    .reduce((ac, a) => ({ ...ac, [a]: true }), {} as Record<string, boolean>);
  return segmentsAreWithin;
};

export const temperatureRangeColours: Record<string, string> = {
  combing: "#f04040",
  fullFuse: "#f0c0c0",
  kilnCasting: "blue",
  kilnCarving: "green",
  strip: "yellow",
  tackFuse: "#912349",
  sagging: "#abab00",
  fuseToStick: "#ab00ab",
  slumping: "#00b040",
  painting: "#10b030",
  devit: "#c00000",
  anneal: "lightGrey",
  strain: "lightGreen",
  draping: "lightBlue",
};
export const getTemperatureRanges = (
  glass: string | null = "Bullseye",
  degrees: TemperatureUnit = "celsius"
): Record<string, number[]> => {
  if (!glass) {
    return {};
  }
  return _.mapValues(temperatureRanges[glass], (value) =>
    convertTemperatures("celsius", degrees, value)
  );
};

export const convertLengthUnit = (
  sourceUnit: LengthUnit = "mm",
  destUnit: LengthUnit,
  length: number
): number | string => {
  if (sourceUnit === destUnit) {
    return length;
  }
  if (sourceUnit === "mm" && destUnit === "in") {
    return (length / 25.4).toFixed(2);
  }
  if (sourceUnit === "in" && destUnit === "mm") {
    return Math.round(length * 25.4);
  }
  return length;
};

export const lengthName = (length_unit?: string): string => {
  switch (length_unit) {
    case "in":
      return "Inches";
    case "mm":
      return "Millimetres";
    // deepcode ignore DuplicateCaseBody: In place in case we need more units later
    default:
      return "Millimetres";
  }
};
